import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { useAuth } from '../../hooks/useAuth';
import { COPILOT_CONFIG } from './config';
import { useAgent } from './useAgent';
import CopilotMessage from './CopilotMessage';
import { PageContext } from './types';
import './Copilot.scss';

const SUGGESTIONS = [
  'Show overdue workflows sorted by due date',
  'Which workflows are in Needs Meeting?',
  'How are reviews trending this quarter?',
];

// Playful, rotating status phrases shown while a (possibly slow) request is in flight.
const LOADING_PHRASES = [
  'Thinking…',
  'Digging through workflows…',
  'Marinating…',
  'Crunching the numbers…',
  'Reviewing the audit trail…',
  'Baking your answer…',
  'Connecting the dots…',
  'Almost there…',
];

const FOLLOW_UPS = [
  'Any compliance red flags?',
  'Export overdue workflows to Excel',
  'How are reviews trending?',
];

const DEFAULT_W = 520;
const DEFAULT_H = 640;
const MIN_W = 360;
const MIN_H = 380;
const STORE_KEY = 'copilotWin';

interface WinState { x: number; y: number; width: number; height: number; }

// Derive the workflow the user is currently viewing from the URL, so Clara understands
// "this workflow / this asset / this version" without the user typing an ID.
const parsePageContext = (pathname: string): PageContext | null => {
  const studio = pathname.match(/\/annotation-studio\/([^/]+)\/([^/]+)/);
  if (studio) {
    const assetId = studio[2];
    const dash = assetId.lastIndexOf('-');
    return {
      route: 'annotation-studio',
      workflow_id: studio[1],
      material_file_id: dash > 0 ? assetId.slice(0, dash) : assetId,
      version: dash > 0 ? assetId.slice(dash + 1) : undefined,
    };
  }
  const wf = pathname.match(/\/workflow\/(?:edit|history)\/([^/]+)/);
  if (wf) return { route: 'workflow', workflow_id: wf[1] };
  return null;
};

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

const loadWin = (): WinState => {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return { x: -1, y: -1, width: DEFAULT_W, height: DEFAULT_H };
};

const saveWin = (w: WinState) => {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(w));
  } catch {
    /* ignore */
  }
};

/**
 * Self-contained "Ask Clara" floating window. Non-modal (no overlay / click-through),
 * draggable by its header, resizable, minimisable to a bubble, and full-screen toggle.
 * Renders nothing unless COPILOT_CONFIG.ENABLED and an ADMIN user is logged in.
 * Mounted once from App.tsx behind a guarded block — delete that block + this folder to remove.
 */
const CopilotPanel = () => {
  const [minimized, setMinimized] = useState(true);
  const [fullscreen, setFullscreen] = useState(false);
  const [win, setWin] = useState<WinState>(loadWin);
  const [input, setInput] = useState('');
  const [phraseIndex, setPhraseIndex] = useState(0);
  const { user } = useAuth();
  const {
    messages, loading, send, identity, loadIdentity, reset,
  } = useAgent();
  const navigate = useNavigate();
  const location = useLocation();
  const pageContext = useMemo(() => parsePageContext(location.pathname), [location.pathname]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const winRef = useRef(win);
  const dragRef = useRef<{ mode: 'drag' | 'resize' | null; sx: number; sy: number; ox: number; oy: number; ow: number; oh: number }>(
    { mode: null, sx: 0, sy: 0, ox: 0, oy: 0, ow: 0, oh: 0 },
  );

  useEffect(() => { winRef.current = win; }, [win]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  // On open: identity, initial/clamped position, scroll to newest.
  useEffect(() => {
    if (minimized) return undefined;
    loadIdentity();
    setWin((w) => {
      let { x, y } = w;
      if (x < 0 || y < 0) {
        x = Math.max(16, window.innerWidth - w.width - 24);
        y = Math.max(16, window.innerHeight - w.height - 24);
      }
      x = clamp(x, 0, Math.max(0, window.innerWidth - 120));
      y = clamp(y, 0, Math.max(0, window.innerHeight - 48));
      return { ...w, x, y };
    });
    const t = setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }), 60);
    return () => clearTimeout(t);
  }, [minimized, loadIdentity]);

  // Rotate the loading phrase every couple of seconds while a request is running.
  useEffect(() => {
    if (!loading) {
      setPhraseIndex(0);
      return undefined;
    }
    const id = setInterval(() => {
      setPhraseIndex((i) => (i + 1) % LOADING_PHRASES.length);
    }, 2200);
    return () => clearInterval(id);
  }, [loading]);

  // Global drag / resize handlers.
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d.mode) return;
      if (d.mode === 'drag') {
        const x = clamp(d.ox + (e.clientX - d.sx), 0, window.innerWidth - 120);
        const y = clamp(d.oy + (e.clientY - d.sy), 0, window.innerHeight - 48);
        setWin((w) => ({ ...w, x, y }));
      } else {
        const width = clamp(d.ow + (e.clientX - d.sx), MIN_W, Math.round(window.innerWidth * 0.95));
        const height = clamp(d.oh + (e.clientY - d.sy), MIN_H, Math.round(window.innerHeight * 0.92));
        setWin((w) => ({ ...w, width, height }));
      }
    };
    const onUp = () => {
      if (dragRef.current.mode) {
        dragRef.current.mode = null;
        document.body.style.userSelect = '';
        saveWin(winRef.current);
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Toggled from the left-sidebar launcher (AskClaraLauncher dispatches this window event).
  // Using an event keeps the panel decoupled from the sidebar — no shared context.
  useEffect(() => {
    const toggle = () => setMinimized((m) => !m);
    window.addEventListener('ask-clara:toggle', toggle);
    return () => window.removeEventListener('ask-clara:toggle', toggle);
  }, []);

  // Broadcast open/closed state so the sidebar launcher can reflect it (active styling) and
  // stay in sync no matter how the panel is closed (launcher click or the window's minimize).
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ask-clara:state', { detail: { open: !minimized } }));
  }, [minimized]);

  // Ask Clara is available to admin users only. `user.role` is set at login (Native + SSO)
  // and read from AuthProvider, which wraps App — unlike RoleProvider, which is nested in Layout.
  if (!COPILOT_CONFIG.ENABLED || !user || !COPILOT_CONFIG.ALLOWED_ROLES.includes(user.role)) return null;

  const name = identity?.firstName || (user && (user.first_name || user.firstName)) || '';
  const welcomeTitle = name ? `Hi ${name} 👋` : 'How can I help?';
  const welcomeSub = name
    ? "I'm Clara. Ask me about your workflows, due dates, audit history or review metrics."
    : 'Ask about workflows, due dates, audit history or review metrics.';

  // When closed, render nothing — the launcher now lives in the left sidebar
  // (AskClaraLauncher), so the panel no longer floats a bubble that can overlap page content.
  if (minimized) return null;

  const startDrag = (e: React.MouseEvent) => {
    if (fullscreen) return;
    if ((e.target as HTMLElement).closest('button')) return; // don't drag when hitting an action
    dragRef.current = {
      mode: 'drag', sx: e.clientX, sy: e.clientY, ox: win.x, oy: win.y, ow: win.width, oh: win.height,
    };
    document.body.style.userSelect = 'none';
  };

  const startResize = (e: React.MouseEvent) => {
    e.stopPropagation();
    dragRef.current = {
      mode: 'resize', sx: e.clientX, sy: e.clientY, ox: win.x, oy: win.y, ow: win.width, oh: win.height,
    };
    document.body.style.userSelect = 'none';
  };

  const submit = () => {
    if (!input.trim() || loading) return;
    send(input, pageContext);
    setInput('');
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const onCitation = (path: string) => {
    setMinimized(true);
    navigate(path);
  };

  return (
    <div
      className={`copilot-window${fullscreen ? ' copilot-window-full' : ''}`}
      style={fullscreen ? undefined : {
        left: win.x, top: win.y, width: win.width, height: win.height,
      }}
    >
      {/* header = drag handle */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className="copilot-winhead" onMouseDown={startDrag}>
        <span className="copilot-title">
          <i className="pi pi-sparkles" />
          {' '}
          Ask Clara
          {identity && <span className="copilot-identity">{`as ${identity.role}`}</span>}
        </span>
        <div className="copilot-header-actions">
          {messages.length > 0 && (
            <button type="button" className="copilot-newchat" title="New chat" onClick={reset}>
              <i className="pi pi-plus" />
              New chat
            </button>
          )}
          <button
            type="button"
            className="copilot-iconbtn"
            title={fullscreen ? 'Exit full screen' : 'Full screen'}
            aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
            onClick={() => setFullscreen((f) => !f)}
          >
            <i className={`pi ${fullscreen ? 'pi-window-minimize' : 'pi-window-maximize'}`} />
          </button>
          <button
            type="button"
            className="copilot-iconbtn"
            title="Minimize"
            aria-label="Minimize"
            onClick={() => setMinimized(true)}
          >
            <i className="pi pi-minus" />
          </button>
        </div>
      </div>

      <div className="copilot-body">
        <div className="copilot-messages" ref={scrollRef}>
          {messages.length === 0 && (
            <div className="copilot-empty">
              <div className="copilot-empty-icon">
                <i className="pi pi-sparkles" />
              </div>
              <h3 className="copilot-empty-title">{welcomeTitle}</h3>
              <p className="copilot-empty-sub">{welcomeSub}</p>
              <div className="copilot-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" className="copilot-suggestion" onClick={() => send(s, pageContext)}>
                    <span>{s}</span>
                    <i className="pi pi-arrow-right" />
                  </button>
                ))}
              </div>
              {pageContext && (
                <p className="copilot-context-hint">
                  <i className="pi pi-eye" />
                  {pageContext.version
                    ? ` Viewing this asset (version ${pageContext.version}) — ask about it directly.`
                    : ' Viewing this workflow — ask about it directly.'}
                </p>
              )}
              <p className="copilot-note">Preview • read-only • answers are scoped to your access.</p>
            </div>
          )}

          {messages.map((m, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <CopilotMessage key={i} message={m} onCitation={onCitation} />
          ))}

          {loading && (
            <div className="copilot-row is-assistant">
              <div className="copilot-avatar">
                <i className="pi pi-sparkles" />
              </div>
              <div className="copilot-bubble copilot-typing">
                <span className="copilot-typing-dots">
                  <span className="copilot-dot" />
                  <span className="copilot-dot" />
                  <span className="copilot-dot" />
                </span>
                <span className="copilot-typing-text">{LOADING_PHRASES[phraseIndex]}</span>
              </div>
            </div>
          )}

          {!loading && messages.length > 0 && messages[messages.length - 1].role === 'assistant' && (
            <div className="copilot-followups">
              {FOLLOW_UPS.map((f) => (
                <button key={f} type="button" className="copilot-followup" onClick={() => send(f, pageContext)}>
                  {f}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="copilot-input">
          <InputTextarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            rows={2}
            autoResize
            placeholder="Ask Clara…  (Enter to send)"
            disabled={loading}
          />
          <Button
            icon="pi pi-send"
            onClick={submit}
            disabled={loading || !input.trim()}
            aria-label="Send"
          />
        </div>
      </div>

      {!fullscreen && (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <div className="copilot-corner" onMouseDown={startResize} />
      )}
    </div>
  );
};

export default CopilotPanel;
